import { useState, useEffect, useMemo } from 'react';
import { apiPath, eventPath } from 'presentations/routes/path';
import { get, patch } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  EventCancelBody,
  SharedEventDetailItem,
} from 'presentations/api/schema/event';
import { useRouter } from 'next/router';
import * as tracker from 'presentations/views/helpers/analytics';
import { ErrorList } from 'domains/models';
import {
  ConfirmationType,
  shouldEventTypeRedirect,
} from 'domains/models/event_type/confirmationType';
import { SharedEventTypeItem } from 'presentations/api/schema/event_type';
import { addEventTypeRedirectProps } from 'presentations/views/helpers/event_type';
import { WebhookEvent } from 'domains/models/webhook/webhookEvent';
import { getDateTime } from 'infras/datetime';

interface Props {
  viewer: ViewerItem | null;
  event: SharedEventDetailItem;
  eventType: SharedEventTypeItem;
}

export function useEventCancellationPage(props: Props) {
  const [cancelEvent, setCancelEvent] = useState<any | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorList>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const router = useRouter();
  const currentUserId = router.query.user ? String(router.query.user) : null;

  const isPast = useMemo(() => {
    if (!props.event) return false;
    return getDateTime(props.event.startedAt) < getDateTime();
  }, [props.event]);

  const onCancelEvent = async () => {
    if (!cancelEvent) return;
    const data: EventCancelBody = { cancelReason: message };
    setIsLoading(true);
    const result = await patch(
      apiPath(`/v1/events/${cancelEvent.uid}/cancel`),
      { data },
    );
    if (!result.isSuccess) {
      if (result.status === 422) {
        setErrorMessage(result.errorMessage ?? null);
      }
      setErrors(result.errors);
      setIsLoading(false);
      return;
    }

    tracker.setEvent('event cancel', {
      locationId: cancelEvent.locationId,
    });

    if (
      shouldEventTypeRedirect(props.eventType.cancelConfirmationType) &&
      props.eventType.cancelConfirmRedirectUrl
    ) {
      let url = props.eventType.cancelConfirmRedirectUrl;
      if (
        props.eventType.cancelConfirmationType ===
        ConfirmationType.RedirectWithProps
      ) {
        url = addEventTypeRedirectProps(
          WebhookEvent.GuestCancelled,
          url,
          props.eventType,
          props.event,
        );
      }
      const redirectUrl = url;
      window.location.href = redirectUrl;
    } else {
      router.push(eventPath(cancelEvent.uid));
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e.currentTarget;
    switch (name) {
      case 'message':
        setErrors({});
        setMessage(value);
        break;
      default:
        break;
    }
  };

  const onCloseErrorMessage = () => {
    setErrorMessage(null);
  };

  return {
    state: {
      currentUserId,
      cancelEvent,
      errors,
      isLoading,
      isPast,
      errorMessage,
    },
    actions: {
      onCancelEvent,
      setCancelEvent,
      onChange,
      onCloseErrorMessage,
    },
  };
}
